/**
 * Map Component
 * Renders the interactive map with origin marker and animated isochrone layers.
 */

import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import type { Coordinates, MapLanguage } from '../types';
import { getApiConfig } from '../config/api';
import styles from './Map.module.css';

/**
 * Props for Map Component
 */
export interface MapProps {
  /** Origin coordinates or null if not set */
  origin: Coordinates | null;
  /** Callback when user clicks on map */
  onLocationClick?: (coords: Coordinates) => void;
  /** Callback to get map instance for layer rendering */
  onMapReady?: (map: mapboxgl.Map) => void;
  /** Map style - 'streets' or 'satellite' */
  mapStyle?: 'streets' | 'satellite';
  /** Map language/locale */
  language?: MapLanguage;
}

/**
 * Map Component
 * Renders Mapbox GL JS map with origin selection and isochrone layer rendering.
 */
export const Map: React.FC<MapProps> = ({
  origin,
  onLocationClick,
  onMapReady,
  mapStyle = 'streets',
  language = 'en',
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const languagePlugin = useRef<MapboxLanguage | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    // Initialize map only once
    if (!mapContainer.current || mapInstance.current) {
      return;
    }

    try {
      const config = getApiConfig();
      mapboxgl.accessToken = config.mapboxToken;

      // Determine map style
      const styleUrl =
        mapStyle === 'satellite'
          ? 'mapbox://styles/mapbox/satellite-v9'
          : 'mapbox://styles/mapbox/streets-v12';

      // Initialize map
      const map = new mapboxgl.Map({
        container: mapContainer.current!,
        style: styleUrl,
        center: [0, 0], // Default center
        zoom: 2,
      });

      mapInstance.current = map;

      // Initialize language plugin only for vector styles (streets), not raster (satellite)
      // MapboxLanguage only works with vector tile version 8+ styles
      if (mapStyle === 'streets') {
        const langPlugin = new MapboxLanguage({
          defaultLanguage: language,
        });
        map.addControl(langPlugin);
        languagePlugin.current = langPlugin;
      }

      // Wait for map to load
      map.on('load', () => {
        setMapLoaded(true);
        if (onMapReady) {
          onMapReady(map);
        }
      });

      // Handle map errors
      map.on('error', (e) => {
        // Only log errors when VITE_DEBUG is enabled, and filter out known non-critical errors
        if (import.meta.env.VITE_DEBUG === 'true') {
          // Filter out expression evaluation errors (known Mapbox style issues)
          const errorMessage = e.error?.message || '';
          if (!errorMessage.includes('evaluated to null') && 
              !errorMessage.includes('vector tile version')) {
            console.error('Mapbox error:', e);
          }
        }
      });
      
      // Track map movements in PostHog
      map.on('moveend', async () => {
        const { trackMapEvent } = await import('../services/posthogService');
        trackMapEvent('move', map);
      });

      // Handle map clicks for origin selection
      map.on('click', async (e) => {
        const coords: Coordinates = {
          lat: e.lngLat.lat,
          lng: e.lngLat.lng,
        };
        if (onLocationClick) {
          onLocationClick(coords);
        }
        
        // Track map click in PostHog
        const { trackMapEvent } = await import('../services/posthogService');
        trackMapEvent('click', map, {
          clicked_location: coords,
        });
      });

      // Cleanup on unmount
      return () => {
        if (marker.current) {
          marker.current.remove();
        }
        if (mapInstance.current) {
          mapInstance.current.remove();
          mapInstance.current = null;
        }
      };
    } catch (error) {
      console.error('Failed to initialize map:', error);
      // Re-throw to be caught by error boundary
      throw error;
    }
  }, [onLocationClick, onMapReady, mapStyle, language]);


  // Update map style when it changes
  useEffect(() => {
    if (!mapInstance.current || !mapLoaded) {
      return;
    }

    const styleUrl =
      mapStyle === 'satellite'
        ? 'mapbox://styles/mapbox/satellite-v9'
        : 'mapbox://styles/mapbox/streets-v12';

    // Remove language plugin if switching to satellite (raster style)
    if (mapStyle === 'satellite' && languagePlugin.current) {
      try {
        mapInstance.current.removeControl(languagePlugin.current);
        languagePlugin.current = null;
      } catch (error) {
        // Plugin might not be added, ignore error
      }
    }

    // Add language plugin if switching to streets (vector style)
    // Note: Language is handled separately in the language update useEffect
    if (mapStyle === 'streets' && !languagePlugin.current) {
      const langPlugin = new MapboxLanguage({
        defaultLanguage: language,
      });
      try {
        mapInstance.current.addControl(langPlugin);
        languagePlugin.current = langPlugin;
      } catch (error) {
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.error('Failed to add language plugin:', error);
        }
      }
    }

    mapInstance.current.setStyle(styleUrl);
    
    // Track map style change in PostHog
    (async () => {
      const { trackEvent } = await import('../services/posthogService');
      trackEvent('map_style_changed', {
        new_style: mapStyle,
      });
    })();
  }, [mapStyle, mapLoaded]); // Removed language from dependencies - language updates are handled separately

  // Update language when it changes (only for vector styles)
  useEffect(() => {
    if (!mapInstance.current || !mapLoaded || mapStyle === 'satellite') {
      return;
    }

    // Ensure language plugin exists for streets style
    if (!languagePlugin.current && mapStyle === 'streets') {
      try {
        const langPlugin = new MapboxLanguage({
          defaultLanguage: language,
        });
        mapInstance.current.addControl(langPlugin);
        languagePlugin.current = langPlugin;
      } catch (error) {
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.error('Failed to add language plugin:', error);
        }
        return;
      }
    }

    if (!languagePlugin.current) {
      return;
    }

    // Check if style is loaded before trying to get it
    const updateLanguage = () => {
      // Capture current value - check if it's not streets (i.e., it's satellite)
      const currentMapStyle = mapStyle;
      if (!mapInstance.current || !languagePlugin.current || currentMapStyle !== 'streets') {
        return;
      }

      try {
        // Check if style is loaded
        if (!mapInstance.current.isStyleLoaded()) {
          // Wait for style to load
          mapInstance.current.once('style.load', updateLanguage);
          return;
        }

        // Update language using the plugin (only for vector styles)
        const currentStyle = mapInstance.current.getStyle();
        if (currentStyle && mapStyle === 'streets') {
          const modifiedStyle = languagePlugin.current.setLanguage(currentStyle, language);
          mapInstance.current.setStyle(modifiedStyle);
        }
      } catch (error) {
        // Silently handle errors - language plugin might not work with all styles
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.error('Failed to update language:', error);
        }
        // If style is not loaded, wait for it
        if (mapInstance.current) {
          mapInstance.current.once('style.load', updateLanguage);
        }
      }
    };

    updateLanguage();
  }, [language, mapLoaded, mapStyle]);

  // Update origin marker when origin changes
  useEffect(() => {
    if (!mapInstance.current || !mapLoaded || !origin) {
      return;
    }

    // Remove existing marker
    if (marker.current) {
      marker.current.remove();
    }

    // Create new marker
    const el = document.createElement('div');
    el.className = styles.marker;

    marker.current = new mapboxgl.Marker(el)
      .setLngLat([origin.lng, origin.lat])
      .addTo(mapInstance.current);

    // Center map on origin
    mapInstance.current.flyTo({
      center: [origin.lng, origin.lat],
      zoom: 13,
      duration: 1000,
    });
  }, [origin, mapLoaded]);

  return (
    <div className={styles.mapContainer} role="application" aria-label="Timed map" data-intro-step="2">
      <div ref={mapContainer} className={styles.map} aria-label="Interactive map" />
    </div>
  );
};

/**
 * Get map instance for external use (e.g., layer rendering)
 */
export function getMapInstance(): mapboxgl.Map | null {
  // This will be used by other components
  // For now, we'll rely on the onMapReady callback
  return null;
}

